import { useEffect, useMemo, useRef, useState } from "react";
import Reveal from "@/components/Reveal";
import BrandLogo from "@/components/BrandLogo";
import {
  BookOpen,
  ShieldAlert,
  Swords,
  HeartHandshake,
  Users,
  ServerCog,
  ChevronRight,
  AlertTriangle,
  Sparkles,
  Crown,
  ChevronLeft,
  Search,
  X,
  Shield,
  Zap,
  Map as MapIcon,
} from "lucide-react";
import { TID } from "@/lib/testIds";
import { useSiteContent, useLinks } from "@/admin/SiteContent";

/**
 * IL CODICE DI PURELIFE — Tomo Olografico di Lusso
 * - Tilt 3D + glare morbido
 * - True 3D page-turn animation in both directions
 * - Synthesised page-turn + chime via Web Audio API
 * - Contenuto dinamico via CMS (`content.rules`)
 */

const ICON_MAP = {
  BookOpen,
  ShieldAlert,
  Swords,
  HeartHandshake,
  Users,
  ServerCog,
  AlertTriangle,
  Sparkles,
  Crown,
  Shield,
  Zap,
  Map: MapIcon,
};

const FALLBACK_CHAPTERS = [
  {
    key: "accesso",
    icon: "BookOpen",
    title: "Accesso & Whitelist",
    subtitle: "Capitolo I",
    intro:
      "L'accesso a PURELIFE è gestito tramite Whitelist. Selezioniamo giocatori maturi e appassionati di roleplay di qualità.",
    sections: [],
  },
];


const PAGE_ANIM_MS = 720;
const ROMAN = ["I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X", "XI", "XII"];

/* ===== Keyword highlight ===== */
const KW_DANGER = [
  "Ban permanente",
  "RDM",
  "VDM",
  "Combat Logging",
  "Doxxing",
  "Powergaming",
  "Metagaming",
  "Cop Baiting",
  "Fail RP",
];
const KW_GOLD = [
  "Whitelist",
  "Roleplay",
  "Fear RP",
  "NLR",
  "New Life Rule",
  "IC",
  "OOC",
  "Senior Staff",
  "PURELIFE",
];
const ALL_KW = [...KW_DANGER, ...KW_GOLD].sort((a, b) => b.length - a.length);
const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const KW_RE = new RegExp(`(${ALL_KW.map(escapeRe).join("|")})`, "g");

const isDanger = (w) => KW_DANGER.some((k) => k.toLowerCase() === w.toLowerCase());

const renderRich = (text) => {
  if (!text) return text;
  const parts = String(text).split(KW_RE);
  return parts.map((p, i) => {
    if (!p) return null;
    if (ALL_KW.some((k) => k.toLowerCase() === p.toLowerCase())) {
      return (
        <span key={i} className={`kw ${isDanger(p) ? "kw--danger" : ""}`}>
          {p}
        </span>
      );
    }
    return p;
  });
};

/* Render text with both keyword glow + active search hit highlight */
const renderText = (text, query) => {
  if (!text) return text;
  const str = String(text);
  if (!query || query.length < 2) return renderRich(str);
  const q = query.toLowerCase();
  const re = new RegExp(`(${escapeRe(query)})`, "gi");
  const parts = str.split(re);
  return parts.map((p, i) => {
    if (!p) return null;
    if (p.toLowerCase() === q) {
      return (
        <mark key={i} className="search-hit">
          {p}
        </mark>
      );
    }
    return <span key={i}>{renderRich(p)}</span>;
  });
};

/* Build a searchable index per chapter */
const chapterCorpus = (c) => {
  const parts = [c.title, c.subtitle, c.intro];
  c.sections.forEach((s) => {
    if (s.h) parts.push(s.h);
    if (s.body) parts.push(s.body);
    if (s.list) parts.push(...s.list);
    if (s.defs) s.defs.forEach((d) => parts.push(d.t, d.d));
    if (s.table) {
      parts.push(...s.table.head);
      s.table.rows.forEach((row) => parts.push(...row));
    }
    if (s.robberies)
      s.robberies.forEach((r) => parts.push(r.name, r.sub, ...r.rules));
  });
  return parts.join(" ").toLowerCase();
};
const CORPUS_FROM = (chapters) =>
  chapters.map((c) => ({ key: c.key, text: chapterCorpus(c) }));

const findFirstChapterWith = (query, corpus) => {
  if (!query || query.length < 2) return null;
  const q = query.toLowerCase();
  const hit = corpus.find((c) => c.text.includes(q));
  return hit ? hit.key : null;
};

/* ===== Section renderer ===== */
const Section = ({ s, index, query }) => (
  <div className={`mb-12 book-section book-section--${Math.min(index + 1, 6)}`}>
    <h4 className="font-display text-2xl sm:text-3xl uppercase text-[#F5F5F5] tracking-wide mb-5 flex items-center gap-3">
      <span className="w-1.5 h-6 bg-[#D4AF37] rounded-full inline-block shadow-[0_0_10px_rgba(212,175,55,0.7)]" />
      {renderText(s.h, query)}
    </h4>
    {s.body && (
      <p className="text-[#B6B6BE] text-lg sm:text-xl leading-loose">
        {renderText(s.body, query)}
      </p>
    )}
    {s.list && (
      <ul className="space-y-4">
        {s.list.map((it, i) => (
          <li
            key={i}
            className="flex items-start gap-3 text-[#B6B6BE] text-lg sm:text-xl leading-loose group"
          >
            <span className="mt-3 w-1.5 h-1.5 rounded-full bg-[#D4AF37] shrink-0 group-hover:shadow-[0_0_10px_#D4AF37] transition-shadow" />
            <span className="group-hover:text-[#F5F5F5] transition-colors">
              {renderText(it, query)}
            </span>
          </li>
        ))}
      </ul>
    )}
    {s.defs && (
      <dl className="grid sm:grid-cols-2 gap-4 mt-1">
        {s.defs.map((d, i) => (
          <div
            key={i}
            className="card-wow rounded-xl p-6 hover:border-[#00D4FF]/40 transition-colors"
          >
            <dt className="font-display text-base uppercase tracking-wider text-[#00D4FF]">
              {renderText(d.t, query)}
            </dt>
            <dd className="mt-2 text-[#B6B6BE] text-lg leading-loose">
              {renderText(d.d, query)}
            </dd>
          </div>
        ))}
      </dl>
    )}
    {s.table && (
      <div className="mt-3 rounded-2xl overflow-hidden border border-white/8 bg-[#0B0B0F]/50 shadow-[0_30px_60px_-30px_rgba(0,0,0,0.85)]">
        <div className="overflow-x-auto">
          <table className="sanction-table w-full text-base">
            <thead>
              <tr className="bg-gradient-to-r from-[#1A1A22]/90 to-[#14141B]/90">
                {s.table.head.map((th, i) => (
                  <th
                    key={i}
                    className="text-left px-5 sm:px-6 py-4 text-[11px] uppercase tracking-[0.22em] text-[#D4AF37] font-mono-pl font-medium border-b border-[#D4AF37]/20"
                  >
                    {renderText(th, query)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {s.table.rows.map((row, r) => (
                <tr
                  key={r}
                  className="border-b border-white/[0.04] last:border-0"
                >
                  {row.map((cell, c) => (
                    <td
                      key={c}
                      className={`px-5 sm:px-6 py-4 ${
                        c === 0
                          ? "text-[#F5F5F5] font-semibold"
                          : "text-[#B6B6BE]"
                      }`}
                    >
                      {renderText(cell, query)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )}
    {s.robberies && (
      <div className="grid sm:grid-cols-2 gap-5 mt-2">
        {s.robberies.map((rb, i) => (
          <article
            key={i}
            className="card-wow rounded-2xl p-7 hover:border-[#D4AF37]/40 transition-colors"
          >
            <div className="flex items-start justify-between gap-3 mb-5">
              <div>
                <h5 className="font-display text-2xl uppercase text-[#F5F5F5] tracking-wide">
                  {renderText(rb.name, query)}
                </h5>
                <p className="text-[11px] uppercase tracking-[0.22em] text-[#B6B6BE] mt-1">
                  {renderText(rb.sub, query)}
                </p>
              </div>
              <span className="font-mono-pl text-[11px] text-[#D4AF37] px-2.5 py-1 rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/5">
                {String(i + 1).padStart(2, "0")}
              </span>
            </div>

            <div className="grid grid-cols-3 gap-2 mb-5">
              {[
                { k: "Rapinatori", v: rb.stats.rapinatori, c: "text-[#E26B6B] border-[#E26B6B]/30" },
                { k: "Poliziotti", v: rb.stats.poliziotti, c: "text-[#00D4FF] border-[#00D4FF]/30" },
                { k: "Ostaggi", v: rb.stats.ostaggi, c: "text-[#D4AF37] border-[#D4AF37]/35" },
              ].map((st) => (
                <div
                  key={st.k}
                  className={`rounded-xl border ${st.c} bg-[#0B0B0F]/60 px-3 py-3 text-center`}
                >
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#B6B6BE] mb-1">
                    {st.k}
                  </div>
                  <div className="font-display text-lg sm:text-xl text-[#F5F5F5]">
                    {st.v}
                  </div>
                </div>
              ))}
            </div>

            <ul className="space-y-2 pt-4 border-t border-white/8">
              {rb.rules.map((r, j) => (
                <li
                  key={j}
                  className="flex items-start gap-2.5 text-sm sm:text-base text-[#B6B6BE] leading-relaxed"
                >
                  <span className="mt-1.5 w-1 h-1 rounded-full bg-[#D4AF37] shrink-0" />
                  <span>{renderText(r, query)}</span>
                </li>
              ))}
            </ul>
          </article>
        ))}
      </div>
    )}
  </div>
);

/* ===== Page renderer (cover or chapter) ===== */
const PageContent = ({
  pageKey,
  chapter,
  activeIndex,
  chaptersCount,
  onPrev,
  onNext,
  onOpen,
  isOpening,
  query,
}) => {
  if (pageKey === "__cover__") {
    return (
      <div className="relative h-full w-full min-h-[640px] lg:min-h-0 book-cover flex flex-col items-center justify-center px-10 sm:px-16 lg:px-24 py-12 text-center">
        <div
          className="absolute inset-0 opacity-[0.07] pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle, #D4AF37 1px, transparent 1.5px)",
            backgroundSize: "70px 70px",
          }}
        />
        <span className="font-mono-pl text-[11px] uppercase tracking-[0.42em] text-[#D4AF37] mb-8">
          Volumen Primum
        </span>
        <div className="relative mb-10">
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#1A1A22] to-[#0B0B0F] border border-[#D4AF37]/40 flex items-center justify-center shadow-[0_30px_60px_-20px_rgba(212,175,55,0.4)]">
            <Crown size={32} className="text-[#D4AF37]" strokeWidth={1.4} />
          </div>
          <div
            className="absolute inset-0 rounded-2xl pointer-events-none"
            style={{
              boxShadow:
                "0 0 40px rgba(212,175,55,0.45), 0 0 100px rgba(212,175,55,0.25)",
            }}
          />
        </div>
        <div className="relative">
          <div
            className="absolute -inset-12 rounded-full pointer-events-none"
            style={{
              background:
                "radial-gradient(closest-side, rgba(212,175,55,0.18), transparent 70%)",
              filter: "blur(28px)",
            }}
          />
          <BrandLogo size="lg" />
        </div>
        <div className="mt-12 flex items-center gap-3">
          <span className="h-px w-14 bg-[#D4AF37]/60" />
          <Sparkles size={16} className="text-[#D4AF37]" />
          <span className="h-px w-14 bg-[#D4AF37]/60" />
        </div>
        <p className="mt-10 max-w-xl text-[#B6B6BE] text-lg sm:text-xl leading-relaxed">
          {chaptersCount} capitoli. Una sola legge. Il tomo che racconta le regole della
          città, le sue sanzioni e l&apos;arte del roleplay che ti chiediamo di
          rispettare.
        </p>
        <button
          onClick={onOpen}
          data-cursor="hot"
          data-testid="faq-open-tomo"
          disabled={isOpening}
          className="btn-gold inline-flex items-center gap-2 rounded-full px-8 py-4 text-sm font-bold uppercase tracking-wider mt-12 disabled:opacity-60"
        >
          {isOpening ? "Apertura…" : "Sfoglia il regolamento"}
          <ChevronRight size={16} strokeWidth={2.6} />
        </button>
        <p className="absolute bottom-6 left-0 right-0 text-[10px] uppercase tracking-[0.32em] text-[#B6B6BE]/60">
          Edizione MMXXVI · Codice della Città
        </p>
      </div>
    );
  }

  return (
    <div className="relative p-10 sm:p-12 lg:p-16 bg-[#0B0B0F]">
      <div className="flex items-center gap-3 mb-2 book-section book-section--1">
        <span className="font-mono-pl text-[11px] uppercase tracking-[0.32em] text-[#D4AF37]">
          {chapter.subtitle}
        </span>
        <span className="text-[11px] text-[#B6B6BE]/50">·</span>
        <span className="text-[11px] uppercase tracking-[0.28em] text-[#B6B6BE]">
          {activeIndex + 1} / {chaptersCount}
        </span>
      </div>
      <h3 className="book-section book-section--1 mt-2 font-display text-4xl sm:text-5xl lg:text-6xl uppercase text-[#F5F5F5] leading-[1] tracking-wide">
        {renderText(chapter.title, query)}
      </h3>
      <p className="book-section book-section--2 mt-8 text-[#B6B6BE] text-lg sm:text-xl leading-loose max-w-4xl">
        <span className="float-left font-display text-7xl sm:text-8xl text-[#D4AF37] glow-gold mr-3 leading-[0.85] mt-1">
          {chapter.intro.charAt(0)}
        </span>
        {renderText(chapter.intro.slice(1), query)}
      </p>
      <div className="clear-both" />
      <div className="divider-line my-12 book-section book-section--2" />

      {chapter.sections.map((s, i) => (
        <Section key={i} s={s} index={i + 2} query={query} />
      ))}

      <div className="mt-14 pt-8 border-t border-white/8 flex flex-wrap items-center justify-between gap-4">
        <p className="text-xs text-[#B6B6BE] font-mono-pl uppercase tracking-[0.22em]">
          — Pagina {activeIndex + 1} di {chaptersCount} —
        </p>
        <div className="flex gap-2">
          <button
            onClick={onPrev}
            data-cursor="hot"
            data-testid="faq-nav-prev"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] px-5 py-3 rounded-full border border-white/8 text-[#B6B6BE] hover:text-[#F5F5F5] hover:border-white/20 transition-colors"
          >
            <ChevronLeft size={14} /> Precedente
          </button>
          <button
            onClick={onNext}
            disabled={activeIndex === chaptersCount - 1}
            data-cursor="hot"
            data-testid="faq-nav-next"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] px-5 py-3 rounded-full border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            Successivo <ChevronRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};

/* ===== Particle decoration ===== */
const PARTICLES = Array.from({ length: 16 }).map((_, i) => {
  const seed = (i * 9301 + 49297) % 233280;
  const r = seed / 233280;
  const r2 = ((i * 7) % 13) / 13;
  return {
    left: `${(r * 100).toFixed(1)}%`,
    delay: `${(r2 * 8).toFixed(2)}s`,
    duration: `${(11 + r * 9).toFixed(1)}s`,
    dx: `${(r2 * 40 - 20).toFixed(0)}px`,
    size: i % 4 === 0 ? 5 : i % 3 === 0 ? 3 : 4,
  };
});

export const Faq = () => {
  const { content } = useSiteContent();
  const LINKS = useLinks();
  const CHAPTERS = useMemo(() => {
    const raw = content && Array.isArray(content.rules) ? content.rules : null;
    if (!raw || raw.length === 0) return FALLBACK_CHAPTERS;
    return raw.map((c) => ({ ...c, sections: Array.isArray(c.sections) ? c.sections : [] }));
  }, [content]);
  const ORDER = useMemo(() => ["__cover__", ...CHAPTERS.map((c) => c.key)], [CHAPTERS]);
  const CORPUS = useMemo(() => CORPUS_FROM(CHAPTERS), [CHAPTERS]);

  const updatedLabel = useMemo(() => {
    const iso = content && content.updated_at;
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    const now = new Date();
    const sameDay =
      d.getFullYear() === now.getFullYear() &&
      d.getMonth() === now.getMonth() &&
      d.getDate() === now.getDate();
    if (sameDay) {
      return `oggi · ${d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}`;
    }
    return `il ${d.toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" })}`;
  }, [content]);

  const [activeKey, setActiveKey] = useState("__cover__");
  const [outgoing, setOutgoing] = useState(null); // { key, dir }
  const [flashing, setFlashing] = useState(false);
  const [query, setQuery] = useState("");
  const [scrollPct, setScrollPct] = useState(0);
  const wrapRef = useRef(null);
  const articleRef = useRef(null);
  const audioCtxRef = useRef(null);
  const turningRef = useRef(false);
  const lastAutoFlippedQueryRef = useRef("");

  // If the active chapter disappears (e.g., admin deleted it), fall back to cover
  useEffect(() => {
    if (activeKey !== "__cover__" && !CHAPTERS.some((c) => c.key === activeKey)) {
      setActiveKey("__cover__");
    }
  }, [CHAPTERS, activeKey]);

  const active = useMemo(
    () => CHAPTERS.find((c) => c.key === activeKey),
    [activeKey, CHAPTERS],
  );

  const isCover = activeKey === "__cover__";
  const activeIndex = isCover ? -1 : CHAPTERS.findIndex((c) => c.key === activeKey);

  /* ===== Web Audio API ===== */
  const ensureCtx = () => {
    if (!audioCtxRef.current) {
      const Ctor = window.AudioContext || window.webkitAudioContext;
      if (!Ctor) return null;
      audioCtxRef.current = new Ctor();
    }
    const ctx = audioCtxRef.current;
    if (ctx.state === "suspended") ctx.resume().catch(() => {});
    return ctx;
  };

  const playPageTurn = () => {
    const ctx = ensureCtx();
    if (!ctx) return;
    const dur = 0.42;
    const buf = ctx.createBuffer(
      1,
      Math.floor(ctx.sampleRate * dur),
      ctx.sampleRate,
    );
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) {
      const t = i / ctx.sampleRate;
      // soft attack, exponential decay, paper-like noise
      const env =
        Math.exp(-t * 7.5) * (1 - Math.exp(-t * 80)) * (1 + 0.4 * Math.sin(t * 18));
      data[i] = (Math.random() * 2 - 1) * env;
    }
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const bp = ctx.createBiquadFilter();
    bp.type = "bandpass";
    bp.frequency.value = 2100;
    bp.Q.value = 1.1;
    const hp = ctx.createBiquadFilter();
    hp.type = "highpass";
    hp.frequency.value = 900;
    const gain = ctx.createGain();
    gain.gain.value = 0.14;
    src.connect(hp);
    hp.connect(bp);
    bp.connect(gain);
    gain.connect(ctx.destination);
    src.start();
  };

  const playChime = () => {
    const ctx = ensureCtx();
    if (!ctx) return;
    const now = ctx.currentTime;
    // Celestial chime: A5, C#6, E6 — major triad with shimmer
    const notes = [880, 1108.73, 1318.51, 1760];
    notes.forEach((f, i) => {
      const osc = ctx.createOscillator();
      osc.type = "sine";
      osc.frequency.value = f;
      const g = ctx.createGain();
      const start = now + i * 0.07;
      g.gain.setValueAtTime(0, start);
      g.gain.linearRampToValueAtTime(0.09, start + 0.03);
      g.gain.exponentialRampToValueAtTime(0.0001, start + 1.6);
      osc.connect(g);
      g.connect(ctx.destination);
      osc.start(start);
      osc.stop(start + 1.7);
    });
    // subtle sparkle noise
    const dur = 0.6;
    const buf = ctx.createBuffer(
      1,
      Math.floor(ctx.sampleRate * dur),
      ctx.sampleRate,
    );
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) {
      const t = i / ctx.sampleRate;
      const env = Math.exp(-t * 5) * (1 - Math.exp(-t * 60));
      data[i] = (Math.random() * 2 - 1) * env * 0.4;
    }
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const bp = ctx.createBiquadFilter();
    bp.type = "bandpass";
    bp.frequency.value = 4200;
    bp.Q.value = 1.4;
    const sg = ctx.createGain();
    sg.gain.value = 0.06;
    src.connect(bp);
    bp.connect(sg);
    sg.connect(ctx.destination);
    src.start(now + 0.05);
  };

  /* ===== Chapter change with 3D page turn ===== */
  const changeChapter = (newKey) => {
    if (newKey === activeKey || turningRef.current) return;
    const oldIdx = ORDER.indexOf(activeKey);
    const newIdx = ORDER.indexOf(newKey);
    const dir = newIdx > oldIdx ? "next" : "prev";

    const isOpening = activeKey === "__cover__" && newKey !== "__cover__";
    turningRef.current = true;
    setOutgoing({ key: activeKey, dir });
    setActiveKey(newKey);

    if (isOpening) {
      setFlashing(true);
      playChime();
      setTimeout(() => setFlashing(false), 850);
    } else {
      playPageTurn();
    }

    if (articleRef.current) articleRef.current.scrollTop = 0;
    setScrollPct(0);

    window.setTimeout(() => {
      setOutgoing(null);
      turningRef.current = false;
    }, PAGE_ANIM_MS);
  };

  const goPrev = () => {
    if (activeIndex > 0) changeChapter(CHAPTERS[activeIndex - 1].key);
    else if (activeIndex === 0) changeChapter("__cover__");
  };
  const goNext = () => {
    if (isCover) changeChapter(CHAPTERS[0].key);
    else if (activeIndex < CHAPTERS.length - 1)
      changeChapter(CHAPTERS[activeIndex + 1].key);
  };

  /* ===== Search → auto-flip to matching chapter ===== */
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      lastAutoFlippedQueryRef.current = "";
      return;
    }
    const t = window.setTimeout(() => {
      const targetKey = findFirstChapterWith(q, CORPUS);
      if (
        targetKey &&
        targetKey !== activeKey &&
        lastAutoFlippedQueryRef.current !== q
      ) {
        lastAutoFlippedQueryRef.current = q;
        changeChapter(targetKey);
      }
    }, 320);
    return () => window.clearTimeout(t);
  }, [query, activeKey, CORPUS]);

  /* ===== Tilt 3D + glare tracking ===== */
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const prefersReduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (prefersReduced) return;

    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const px = x / rect.width;
      const py = y / rect.height;
      const rx = (px - 0.5) * 5;
      const ry = (0.5 - py) * 3.5;
      el.style.setProperty("--book-rx", `${rx.toFixed(2)}deg`);
      el.style.setProperty("--book-ry", `${ry.toFixed(2)}deg`);
      el.style.setProperty("--mx", `${(px * 100).toFixed(1)}%`);
      el.style.setProperty("--my", `${(py * 100).toFixed(1)}%`);
    };
    const onLeave = () => {
      el.style.setProperty("--book-rx", `0deg`);
      el.style.setProperty("--book-ry", `0deg`);
    };
    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseleave", onLeave);
    return () => {
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  const outgoingChapter =
    outgoing && outgoing.key !== "__cover__"
      ? CHAPTERS.find((c) => c.key === outgoing.key)
      : null;
  const outgoingIndex = outgoing ? CHAPTERS.findIndex((c) => c.key === outgoing.key) : -1;

  return (
    <section
      id="faq"
      data-testid={TID.faq.root}
      className="relative py-24 sm:py-32"
    >
      <div
        className="orb bg-[#D4AF37]"
        style={{ width: 500, height: 500, left: "5%", top: "10%", opacity: 0.1 }}
      />
      <div
        className="orb bg-[#00D4FF]"
        style={{ width: 400, height: 400, right: "5%", bottom: "5%", opacity: 0.08 }}
      />

      <div className="relative mx-auto w-[min(95vw,1800px)] max-w-[1800px] px-5 sm:px-8">
        <div className="text-center mb-14">
          <Reveal>
            <div className="inline-flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-[#D4AF37]/60" />
              <span className="font-mono-pl text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]">
                Tomo Olografico
              </span>
              <span className="h-px w-10 bg-[#D4AF37]/60" />
            </div>
          </Reveal>
          <Reveal delay={1}>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase text-[#F5F5F5] leading-[0.98] tracking-wide">
              Il Codice della <span className="text-gold glow-gold">Città</span>
            </h2>
          </Reveal>
          <Reveal delay={2}>
            <p className="mt-6 text-[#B6B6BE] max-w-2xl mx-auto text-base sm:text-lg">
              Sfoglia il REGOLAMENTO digitale di PURELIFE — {CHAPTERS.length} capitoli, una sola legge.
              Cerca un termine o muovi il mouse sul libro per sentirne la materia.
            </p>
          </Reveal>
          {updatedLabel && (
            <Reveal delay={3}>
              <div
                className="mt-6 inline-flex items-center gap-2.5 px-4 py-2 rounded-full"
                data-testid="faq-updated-badge"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(212,175,55,0.08), rgba(212,175,55,0.02))",
                  border: "1px solid rgba(212,175,55,0.28)",
                  boxShadow:
                    "0 0 22px -10px rgba(212,175,55,0.55), inset 0 0 12px rgba(212,175,55,0.05)",
                }}
              >
                <span className="relative flex w-1.5 h-1.5">
                  <span className="absolute inline-flex w-full h-full rounded-full bg-[#D4AF37] opacity-70 animate-ping" />
                  <span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-[#D4AF37]" />
                </span>
                <span className="font-mono-pl text-[10px] uppercase tracking-[0.28em] text-[#D4AF37]">
                  Aggiornato {updatedLabel}
                </span>
              </div>
            </Reveal>
          )}
        </div>

        <Reveal delay={3}>
          <div className="book-stage">
            <div className="book-particles" aria-hidden="true">
              {PARTICLES.map((p, i) => (
                <span
                  key={i}
                  className="bp"
                  style={{
                    left: p.left,
                    bottom: "-10px",
                    width: `${p.size}px`,
                    height: `${p.size}px`,
                    "--d": p.duration,
                    "--delay": p.delay,
                    "--dx": p.dx,
                  }}
                />
              ))}
            </div>

            <div
              ref={wrapRef}
              className="book-wrap card-wow rounded-3xl overflow-hidden book book-leather-inset"
            >
              <div
                className="book-spine hidden lg:block"
                style={{ left: "calc(340px - 12px)" }}
              />
              <div className="book-edge hidden lg:block" style={{ left: "340px" }} />

              <div className="grid lg:grid-cols-[340px_1fr] lg:h-[85vh] lg:min-h-[900px]">
                {/* Sidebar */}
                <aside className="min-w-0 max-w-full overflow-hidden border-b lg:border-b-0 lg:border-r border-white/8 bg-[#0B0B0F]/70 relative z-10">
                  <div className="p-6 sm:p-7 border-b border-white/8">
                    <p className="font-mono-pl text-[10px] uppercase tracking-[0.28em] text-[#D4AF37]">
                      Indice
                    </p>
                    <h3 className="mt-1 mb-5 font-display text-2xl uppercase text-[#F5F5F5]">
                      Capitoli
                    </h3>

                    {/* === Cyber-Luxury Search === */}
                    <div className="relative">
                      <Search
                        size={15}
                        className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#00D4FF] pointer-events-none"
                        strokeWidth={2.2}
                      />
                      <input
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Cerca nel regolamento…"
                        data-testid="faq-search-input"
                        data-cursor="hot"
                        className="search-input w-full pl-10 pr-9 py-3 rounded-xl bg-[#0B0B0F]/85 border border-[#D4AF37]/35 text-sm text-[#F5F5F5] placeholder:text-[#B6B6BE]/55 focus:outline-none"
                      />
                      {query && (
                        <button
                          onClick={() => setQuery("")}
                          aria-label="Cancella ricerca"
                          data-testid="faq-search-clear"
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-[#B6B6BE] hover:text-[#D4AF37] transition-colors"
                        >
                          <X size={14} strokeWidth={2.4} />
                        </button>
                      )}
                    </div>
                    {query.trim().length >= 2 && (
                      <p className="mt-2.5 text-[10px] uppercase tracking-[0.22em] text-[#B6B6BE]/70">
                        {findFirstChapterWith(query.trim(), CORPUS)
                          ? "Sfoglio automatico in corso…"
                          : "Nessun risultato trovato"}
                      </p>
                    )}
                  </div>

                  <nav className="p-3 sm:p-4 flex lg:flex-col overflow-x-auto lg:overflow-visible gap-1.5">
                    <button
                      onClick={() => changeChapter("__cover__")}
                      data-testid="faq-item-cover"
                      data-cursor="hot"
                      className={`chapter-link group flex items-center gap-3 px-3 sm:px-4 py-3 rounded-xl text-left shrink-0 lg:w-full transition-all duration-300 ${
                        isCover
                          ? "is-active bg-[#D4AF37]/10 border border-[#D4AF37]/40 text-[#F5F5F5]"
                          : "border border-transparent text-[#B6B6BE] hover:text-[#F5F5F5]"
                      }`}
                    >
                      <span
                        className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors ${
                          isCover
                            ? "bg-[#D4AF37]/15 border border-[#D4AF37]/40"
                            : "bg-[#0B0B0F]/60 border border-white/8 group-hover:border-[#00D4FF]/40"
                        }`}
                      >
                        <Crown
                          size={15}
                          className={isCover ? "text-[#D4AF37]" : "text-[#B6B6BE]"}
                          strokeWidth={1.7}
                        />
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-[9px] font-mono-pl uppercase tracking-[0.22em] text-[#B6B6BE]/70 mb-0.5">
                          00
                        </span>
                        <span className="block text-sm font-semibold truncate">
                          Copertina
                        </span>
                      </span>
                      <ChevronRight
                        size={14}
                        className={`shrink-0 transition-all ${
                          isCover
                            ? "text-[#D4AF37]"
                            : "text-[#B6B6BE]/60 -translate-x-1 opacity-0 group-hover:opacity-100 group-hover:translate-x-0"
                        }`}
                      />
                    </button>

                    {CHAPTERS.map((c, i) => {
                      const Icon = ICON_MAP[c.icon] || BookOpen;
                      const isActive = c.key === activeKey;
                      return (
                        <button
                          key={c.key}
                          onClick={() => changeChapter(c.key)}
                          data-testid={TID.faq.item(c.key)}
                          data-cursor="hot"
                          className={`chapter-link group flex items-center gap-3 px-3 sm:px-4 py-3 rounded-xl text-left shrink-0 lg:w-full transition-all duration-300 ${
                            isActive
                              ? "is-active bg-[#D4AF37]/10 border border-[#D4AF37]/40 text-[#F5F5F5]"
                              : "border border-transparent text-[#B6B6BE] hover:text-[#F5F5F5]"
                          }`}
                        >
                          <span
                            className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors ${
                              isActive
                                ? "bg-[#D4AF37]/15 border border-[#D4AF37]/40"
                                : "bg-[#0B0B0F]/60 border border-white/8 group-hover:border-[#00D4FF]/40"
                            }`}
                          >
                            <Icon
                              size={15}
                              className={isActive ? "text-[#D4AF37]" : "text-[#B6B6BE]"}
                              strokeWidth={1.7}
                            />
                          </span>
                          <span className="flex-1 min-w-0">
                            <span className="block text-[9px] font-mono-pl uppercase tracking-[0.22em] text-[#B6B6BE]/70 mb-0.5">
                              {String(i + 1).padStart(2, "0")}
                            </span>
                            <span className="block text-sm font-semibold truncate">
                              {c.title}
                            </span>
                          </span>
                          <ChevronRight
                            size={14}
                            className={`shrink-0 transition-all ${
                              isActive
                                ? "text-[#D4AF37]"
                                : "text-[#B6B6BE]/60 -translate-x-1 opacity-0 group-hover:opacity-100 group-hover:translate-x-0"
                            }`}
                          />
                        </button>
                      );
                    })}
                  </nav>

                  <div className="p-5 sm:p-6 border-t border-white/8">
                    <p className="text-[10px] uppercase tracking-[0.28em] text-[#B6B6BE] mb-3">
                      Hai dubbi?
                    </p>
                    <a
                      href={LINKS.discord}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-ghost-neon inline-flex items-center justify-center w-full rounded-full px-4 py-2.5 text-xs font-semibold"
                    >
                      Apri ticket Discord
                    </a>
                  </div>
                </aside>

                {/* Right page — stage with incoming + outgoing layers */}
                <div className="relative lg:h-full overflow-hidden min-w-0 max-w-full" style={{ isolation: "isolate" }}>
                  {/* Roman numeral watermark — stays visible at bottom-right while content scrolls */}
                  {!isCover && (
                    <span
                      aria-hidden="true"
                      className="roman-watermark"
                    >
                      {ROMAN[activeIndex] || ""}
                    </span>
                  )}
                  <article
                    ref={articleRef}
                    onScroll={(e) => {
                      const el = e.currentTarget;
                      const max = el.scrollHeight - el.clientHeight;
                      const pct = max > 0 ? el.scrollTop / max : 0;
                      setScrollPct(Math.min(1, Math.max(0, pct)));
                    }}
                    className="relative book-scroll lg:h-full lg:overflow-y-auto"
                  >
                    {/* Incoming page */}
                    <div
                      key={activeKey}
                      className={`book-page book-page--in-${outgoing ? outgoing.dir : "next"} lg:min-h-full`}
                    >
                      <PageContent
                        pageKey={activeKey}
                        chapter={active}
                        activeIndex={activeIndex}
                        chaptersCount={CHAPTERS.length}
                        onPrev={goPrev}
                        onNext={goNext}
                        onOpen={() => changeChapter(CHAPTERS[0]?.key || "__cover__")}
                        isOpening={flashing}
                        query={query.trim()}
                      />
                    </div>

                    {/* Outgoing page overlay */}
                    {outgoing && (
                      <div
                        className={`book-page book-page--out-${outgoing.dir} absolute inset-0 z-40 pointer-events-none overflow-hidden`}
                        aria-hidden="true"
                      >
                        <PageContent
                          pageKey={outgoing.key}
                          chapter={outgoingChapter}
                          activeIndex={outgoingIndex}
                          chaptersCount={CHAPTERS.length}
                          onPrev={() => {}}
                          onNext={() => {}}
                          onOpen={() => {}}
                          isOpening={false}
                          query={query.trim()}
                        />
                      </div>
                    )}
                  </article>

                  {/* Scroll progress — golden bar on right edge */}
                  {!isCover && (
                    <div className="book-progress" aria-hidden="true">
                      <span
                        className="book-progress__bar"
                        style={{ height: `${(scrollPct * 100).toFixed(1)}%` }}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Soft holographic glare */}
              <div className="book-glare" aria-hidden="true" />

              {/* Cinematic neon flash on first opening */}
              <div
                className={`book-flash ${flashing ? "is-active" : ""}`}
                aria-hidden="true"
              />
            </div>
          </div>
        </Reveal>

        {/* CTA to open the full dedicated Rules page */}
        <Reveal delay={4}>
          <div className="mt-14 flex flex-col items-center gap-3">
            <a
              href="/regolamento"
              data-testid="faq-open-full-rules"
              className="group inline-flex items-center gap-3 rounded-full px-7 py-3.5 text-[11px] uppercase tracking-[0.26em] border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0B0B0F] transition-all duration-500 shadow-[0_10px_30px_-15px_rgba(212,175,55,0.6)]"
            >
              <BookOpen size={13} strokeWidth={2} />
              <span>Apri il Regolamento completo</span>
              <ChevronRight
                size={13}
                strokeWidth={2.4}
                className="transition-transform duration-500 group-hover:translate-x-1"
              />
            </a>
            <p className="text-[10px] tracking-[0.28em] uppercase text-[#6b6b76]">
              Pagina dedicata · Ricerca · Stampa PDF
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Faq;
